import { reactive } from "vue";
import { api } from "~/api";
import { HasonInfo } from "~/api/data/HasonInfo";
import { KaiyakuForInput } from "~/api/data/KaiyakuForInput";
import { TaikyoEntity } from "~/api/data/TaikyoEntity";
import { TaikyoForInput } from "~/api/data/TaikyoForInput";
import { InsertTaikyo } from "~/api/taikyo/commands/InsertTaikyo";
import { UpdateForComplete } from "~/api/taikyo/commands/UpdateForComplete";
import { UpdateKaiyaku } from "~/api/taikyo/commands/UpdateKaiyaku";
import { Log } from "~/extensions/utils";
import { InsertTaikyoNotification } from "~/api/taikyoNotification/commands/InsertTaikyoNotification";
import { TaikyoNotificationForInput } from "~/api/data/TaikyoNotificationForInput";
import { TaikyoNotificationEntity } from "~/api/data/TaikyoNotificationEntity";
import { TaikyoSave } from "~/api/taikyoNotification/commands/TaikyoSave";
import { FindTaikyoNotificationForInput } from "~/api/taikyoNotification/commands/FindTaikyoNotificationForInput";
import { InsertFile } from "~/api/taikyoNotification/commands/InsertFile";
import { DeleteFile } from "~/api/taikyoNotification/commands/DeleteFile";
import { isNullOrUndefined } from "~/extensions/utils/DefineFunction";
import { RoomSb, SelectRoomSb } from "~/extensions/utils/DefineTypes";
import { FindTaikyoNotificationInfo } from "~/api/taikyoNotification/commands/FindTaikyoNotificationInfo";
const log = Log.create("TaikyoEntryStore");
class Store {
    taikyoEntity = reactive(new TaikyoEntity());
    taikyoNotificationEntity = reactive(new TaikyoNotificationEntity());
    taikyoForInput = reactive(new TaikyoForInput());
    kaiyakuForInput = reactive(new KaiyakuForInput());
    taikyoNotificationForInput = reactive(new TaikyoNotificationForInput());
    hasonInfo = reactive(new HasonInfo());
    privacyConsent = null;
    storedTaikyoNotificationEntityJson = null;
    selectableRooms = reactive([]);
    selectedRooms = reactive([]);
    selectRoomSb = null;
    notFoundNyukyoList = reactive([]);
    showNotFoundNyukyoDialog = false;
    async load() {
        this.taikyoEntity = await api.taikyo.find();
    }
    async loadTaikyoNotification(OrgNyukyokeiyakuNoList) {
        const result = await api.taikyoNotification.findNotificationInfo(FindTaikyoNotificationInfo.create(OrgNyukyokeiyakuNoList));
        Object.assign(this.taikyoNotificationEntity, result);
        this.notFoundNyukyoList = result.notFoundNyukyoList;
        if (this.notFoundNyukyoList.length > 0) {
            this.showNotFoundNyukyoDialog = true;
            this.selectedRooms = this.selectedRooms.filter(x => !this.notFoundNyukyoList.includes(x.orgNyukyokeiyakuNo));
        }
    }
    async loadTaikyoNotificationSave(bulkTaikyoNo) {
        const data = await api.taikyo.findNotificationSaveInfo(bulkTaikyoNo);
        Object.assign(this.taikyoNotificationEntity, data);
    }
    async loadForTaikyo() {
        const data = await api.taikyo.findForInput();
        Object.assign(this.taikyoForInput, data);
    }
    async loadForKaiyaku() {
        this.kaiyakuForInput = await api.kaiyaku.findForInput();
    }
    async loadHasonInfo() {
        this.hasonInfo = await api.kaiyaku.findHasonInfo();
    }
    async loadForTaikyoNotification(orgNyukyokeiyakuNoList, isAdd) {
        const additionalInput = await api.taikyoNotification.findNotificationForInput(FindTaikyoNotificationForInput.create(orgNyukyokeiyakuNoList));
        if (isAdd) {
            this.taikyoNotificationForInput.inputList.push(...additionalInput.inputList);
        }
        else {
            Object.assign(this.taikyoNotificationForInput, additionalInput);
        }
    }
    isNoData() {
        return this.taikyoEntity == null ? true : this.taikyoEntity.taikyoNo === 0;
    }
    isSave() {
        return !isNullOrUndefined(this.taikyoNotificationEntity.bulkTaikyoNo);
    }
    setData(taikyo, input) {
        this.taikyoEntity = taikyo;
        this.taikyoForInput = input;
    }
    setPrivacyConsent(consent) {
        this.privacyConsent = consent ? 1 : 0;
    }
    roomSbCheck(selectRoomSb) {
        if (Array.isArray(selectRoomSb)) {
            // 法人・社宅の場合
            // 全てのルーム種別が部屋の場合
            if (selectRoomSb.every(roomSb => roomSb === RoomSb.Room)) {
                this.selectRoomSb = SelectRoomSb.Room;
                // 全てのルーム種別が駐車場の場合
            }
            else if (selectRoomSb.every(roomSb => roomSb === RoomSb.Parking)) {
                this.selectRoomSb = SelectRoomSb.Parking;
                // ルーム種別に部屋、駐車場が含まれている場合
            }
            else if (selectRoomSb.some(roomSb => roomSb === RoomSb.Room) && selectRoomSb.some(roomSb => roomSb === RoomSb.Parking)) {
                this.selectRoomSb = SelectRoomSb.RoomAndParking;
            }
        }
        else {
            // 個人の場合
            switch (selectRoomSb) {
                case 1:
                    this.selectRoomSb = SelectRoomSb.Room;
                    break;
                case 2:
                    this.selectRoomSb = SelectRoomSb.Parking;
                    break;
                default:
                    break;
            }
        }
        return this.selectRoomSb;
    }
    clear() {
        this.taikyoEntity = new TaikyoEntity();
        this.taikyoForInput = new TaikyoForInput();
        this.taikyoNotificationEntity = new TaikyoNotificationEntity();
        this.taikyoNotificationForInput = new TaikyoNotificationForInput();
        this.kaiyakuForInput = new KaiyakuForInput();
        this.hasonInfo = new HasonInfo();
        this.privacyConsent = null;
        this.storedTaikyoNotificationEntityJson = null;
        this.notFoundNyukyoList = [];
    }
    async insert() {
        await api.taikyo.insert(InsertTaikyo.create(this.taikyoEntity));
    }
    async update(isKouzaChange) {
        await api.kaiyaku.update(UpdateKaiyaku.create(this.taikyoEntity, this.hasonInfo, isKouzaChange));
    }
    async updateForComplete() {
        await api.kaiyaku.updateForComplete(UpdateForComplete.create());
    }
    async insertNotification(isKouzaChange) {
        await api.taikyoNotification.insertNotification(new InsertTaikyoNotification(this.taikyoNotificationEntity, isKouzaChange));
    }
    async insertSave() {
        await api.taikyoNotification.insertSave(new TaikyoSave(this.taikyoNotificationEntity));
    }
    async insertFile(taikyoNo, files, fileInfoList) {
        await api.taikyoNotification.insertFile(InsertFile.create(taikyoNo, files, fileInfoList));
    }
    async deleteFile() {
        await api.taikyoNotification.deleteFile(new DeleteFile(this.taikyoNotificationEntity));
    }
}
export let store = reactive(new Store());
